import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { CreditCard, Smartphone, Search, RotateCcw, List, LayoutGrid, ChevronUp, ChevronDown, ChevronsUpDown, IndianRupee, Calendar, CheckCircle2, XCircle, Clock, TrendingUp, Wallet, Receipt, Activity } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import { Skeleton } from '../../../components/ui/skeleton';

export default function Recharge() {
  const [recharges, setRecharges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('amount');
  const [sortOrder, setSortOrder] = useState('asc');
  const [meta, setMeta] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const { get } = useApi();
  const { user } = useAuth();

  useEffect(() => {
    fetchRecharges();
  }, [currentPage, sortField, sortOrder]);

  // Set default view mode based on screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      setViewMode(isMobile ? 'grid' : 'table');
    };

    // Set initial view mode
    checkScreenSize();

    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchRecharges();
      } else {
        setCurrentPage(1);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleRefresh = () => {
    fetchRecharges();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return sortOrder === 'asc' 
      ? <ChevronUp className="h-4 w-4 text-primary" />
      : <ChevronDown className="h-4 w-4 text-primary" />;
  };

  // Get admin ID from user object
  const getAdminId = () => {
    return user?.id || user?._id || user?.admin_id || user?.userId || user?.user_id || null;
  };

  const fetchRecharges = async () => {
    try {
      setIsLoading(true);
      
      
      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage,
        perPage: itemsPerPage,
        sortBy: sortField,
        sortOrder: sortOrder
      });

      // Add search parameter if exists
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add admin_id if available
      const adminId = getAdminId();
      if (adminId) {
        params.append('admin_id', adminId);
      }

      const response = await get(`/admin/reports/recharges?${params.toString()}`);
      
      
      // Handle the API response structure
      if (response?.success && Array.isArray(response?.data)) {
        setRecharges(response.data);
        setMeta(response.meta);
      } else {
        setRecharges([]);
        setMeta(null);
      }
      
    } catch (error) {
      
      let errorMessage = 'An error occurred while fetching recharges. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.status === 404) {
        errorMessage = 'API endpoint not found. Please check the server configuration.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setRecharges([]);
      setMeta(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    const currency = meta?.currency || 'INR';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch (error) {
      return dateString;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'success') {
      return (
        <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 border-0 px-3 py-1 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Success
        </Badge>
      );
    } else if (statusLower === 'pending') {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100 border-0 px-3 py-1 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Pending
        </Badge>
      );
    } else if (statusLower === 'failed') {
      return (
        <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100 border-0 px-3 py-1 flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          Failed
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100 border-0 px-3 py-1">
        {status || 'Unknown'}
      </Badge>
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Calculate statistics
  const totalAmount = recharges.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const totalCommission = recharges.reduce((sum, r) => sum + (parseFloat(r.commissionAmount || r.commission_amount) || 0), 0);
  const successCount = recharges.filter(r => r.status?.toLowerCase() === 'success').length;

  // Skeleton loading component
  const SkeletonRow = () => (
    <TableRow className="h-12">
      <TableCell className="px-4 py-3">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-4 py-3">
        <Skeleton className="h-4 w-32" />
      </TableCell>
      <TableCell className="px-4 py-3">
        <Skeleton className="h-4 w-32" />
      </TableCell>
      <TableCell className="px-4 py-3 hidden sm:table-cell">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-4 py-3">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-4 py-3 hidden md:table-cell">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-4 py-3">
        <Skeleton className="h-6 w-20 rounded-full" />
      </TableCell>
    </TableRow>
  );

  const totalPages = meta?.lastPage || 1;
  const totalRecharges = meta?.total || 0;

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-7xl bg-background text-foreground min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl  font-bold mb-2">Recharge Transactions</h1>
            <p className="text-sm text-muted-foreground">Manage and track all recharge transactions</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <div className="relative flex-1 sm:flex-initial min-w-[200px]">
              <Input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 border-1 border-muted-foreground/20"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="flex items-center gap-2 border-0 bg-muted/50"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
              className="flex items-center gap-2 border-0 bg-muted/50"
            >
              {viewMode === 'table' ? <LayoutGrid className="h-4 w-4" /> : <List className="h-4 w-4" />}
              <span className="hidden sm:inline">{viewMode === 'table' ? 'Grid' : 'Table'}</span>
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Balance Card */}
          <Card className="border-0 shadow-md bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-background hover:shadow-lg transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Current Balance</p>
                  {isLoading ? (
                    <Skeleton className="h-8 w-32" />
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <IndianRupee className="h-5 w-5 text-primary" />
                      <h3 className="text-xl font-bold text-foreground">
                        {meta?.balance?.toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }) || '0.00'}
                      </h3>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">{meta?.currency || 'INR'}</p>
                </div>
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-blue-500 text-white">
                    <Wallet className="h-6 w-6" />
                  </AvatarFallback>
                </Avatar>
              </div>
            </CardContent>
          </Card>

          {/* Total Recharges Card */}
          <Card className="border-0 shadow-md bg-muted/50 hover:shadow-lg transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Total Transactions</p>
                  {isLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    <h3 className="text-xl font-bold text-foreground">{totalRecharges}</h3>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">All time</p>
                </div>
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-emerald-500 text-white">
                    <Receipt className="h-6 w-6" />
                  </AvatarFallback>
                </Avatar>
              </div>
            </CardContent>
          </Card>

          {/* Total Amount Card */}
          <Card className="border-0 shadow-md bg-muted/50 hover:shadow-lg transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Total Amount</p>
                  {isLoading ? (
                    <Skeleton className="h-8 w-32" />
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <IndianRupee className="h-5 w-5 text-primary" />
                      <h3 className="text-xl font-bold text-foreground">
                        {totalAmount.toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">This page</p>
                </div>
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-purple-500 text-white">
                    <TrendingUp className="h-6 w-6" />
                  </AvatarFallback>
                </Avatar>
              </div>
            </CardContent>
          </Card>

          {/* Total Cashback Card */}
          <Card className="border-0 shadow-md bg-muted/50 hover:shadow-lg transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Total Cashback</p>
                  {isLoading ? (
                    <Skeleton className="h-8 w-32" />
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <IndianRupee className="h-5 w-5 text-green-600" />
                      <h3 className="text-xl font-bold text-green-600">
                        {totalCommission.toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">This page</p>
                </div>
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-green-500 text-white">
                    <Activity className="h-6 w-6" />
                  </AvatarFallback>
                </Avatar>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Table or Grid View */}
      <Card className="border-0 shadow-md bg-muted/50">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-blue-500 text-white">
                <CreditCard className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-lg">Recharge Transactions</CardTitle>
              <CardDescription>View and manage all recharge transactions</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === 'table' ? (
            <div className="rounded-lg overflow-hidden border-1 border-muted-foreground/20">
              <Table>
                <TableHeader>
                  <TableRow className="h-12 bg-muted/70">
                    <TableHead className="px-4 py-3 text-sm font-semibold">
                      <button
                        onClick={() => handleSort('createdAt')}
                        className="flex items-center gap-1 hover:text-primary transition-colors"
                      >
                        Date
                        {getSortIcon('createdAt')}
                      </button>
                    </TableHead>
                    <TableHead className="px-4 py-3 text-sm font-semibold">
                      <button
                        onClick={() => handleSort('txnId')}
                        className="flex items-center gap-1 hover:text-primary transition-colors"
                      >
                        Transaction ID
                        {getSortIcon('txnId')}
                      </button>
                    </TableHead>
                    <TableHead className="px-4 py-3 text-sm font-semibold">
                      <button
                        onClick={() => handleSort('mobileNumber')}
                        className="flex items-center gap-1 hover:text-primary transition-colors"
                      >
                        Mobile Number
                        {getSortIcon('mobileNumber')}
                      </button>
                    </TableHead>
                    <TableHead className="px-4 py-3 text-sm font-semibold hidden sm:table-cell">
                      Provider
                    </TableHead>
                    <TableHead className="px-4 py-3 text-sm font-semibold">
                      <button
                        onClick={() => handleSort('amount')}
                        className="flex items-center gap-1 hover:text-primary transition-colors"
                      >
                        Amount
                        {getSortIcon('amount')}
                      </button>
                    </TableHead>
                    <TableHead className="px-4 py-3 text-sm font-semibold hidden md:table-cell">
                      Cashback
                    </TableHead>
                    <TableHead className="px-4 py-3 text-sm font-semibold">
                      Status
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <SkeletonRow key={`skeleton-${index}`} />
                    ))
                  ) : recharges.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <CreditCard className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-xl font-semibold mb-2">No recharges found</h3>
                        <p className="text-muted-foreground">
                          {searchTerm ? 'Try adjusting your search criteria' : 'No recharge transactions available'}
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recharges.map((recharge) => (
                      <TableRow key={recharge._id} className="h-12 hover:bg-muted/30 transition-colors">
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{formatDate(recharge.createdAt)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-sm font-medium">{recharge.txnId || recharge.txn_id}</span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Smartphone className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{recharge.mobileNumber || recharge.mobile_number}</span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 hidden sm:table-cell">
                          <Badge variant="outline" className="text-xs">
                            {recharge.provider || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <IndianRupee className="h-4 w-4 text-primary" />
                            <span className="text-sm font-semibold">
                              {(recharge.amount || 0).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 hidden md:table-cell">
                          <span className="text-sm text-green-600 font-medium">
                            {formatCurrency(recharge.commissionAmount || recharge.commission_amount || 0)}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          {getStatusBadge(recharge.status)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            /* Grid View */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, index) => (
                  <Card key={`skeleton-${index}`} className="border-0 bg-background/50">
                    <CardContent className="p-4 space-y-3">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-6 w-16 rounded-full" />
                    </CardContent>
                  </Card>
                ))
              ) : recharges.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <CreditCard className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No recharges found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchTerm ? 'Try adjusting your search criteria' : 'No recharge transactions available'}
                  </p>
                </div>
              ) : (
                recharges.map((recharge) => (
                  <Card 
                    key={recharge._id} 
                    className="border-0 bg-background/50 hover:shadow-lg transition-all duration-300 hover:border-primary/20"
                  >
                    <CardContent className="p-4 space-y-3">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground mb-1">Transaction ID</p>
                          <p className="text-sm font-semibold truncate">{recharge.txnId || recharge.txn_id}</p>
                        </div>
                        {getStatusBadge(recharge.status)}
                      </div>

                      {/* Mobile Number */}
                      <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400">
                            <Smartphone className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground">Mobile Number</p>
                          <p className="text-sm font-medium truncate">{recharge.mobileNumber || recharge.mobile_number || 'N/A'}</p>
                        </div>
                      </div>

                      {/* Provider */}
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400">
                              <Receipt className="h-4 w-4" />
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-xs text-muted-foreground">Provider</span>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {recharge.provider || 'N/A'}
                        </Badge>
                      </div>

                      {/* Amount & Commission */}
                      <div className="space-y-2 p-3 bg-gradient-to-br from-green-500/10 to-transparent rounded-lg border-1 border-muted-foreground/20">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Amount</span>
                          <div className="flex items-center gap-1">
                            <IndianRupee className="h-4 w-4 text-primary" />
                            <span className="text-base font-bold">
                              {(recharge.amount || 0).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        </div>
                        {(recharge.commissionAmount || recharge.commission_amount) && (
                          <div className="flex items-center justify-between pt-2 border-t border-green-500/20">
                            <span className="text-xs text-muted-foreground">Commission</span>
                            <span className="text-sm font-medium text-green-600">
                              {formatCurrency(recharge.commissionAmount || recharge.commission_amount)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Date */}
                      <div className="flex items-center gap-2 pt-2 border-t border-muted-foreground/10">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">
                          {formatDate(recharge.createdAt)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-6 gap-4">
        {isLoading ? (
          <>
            <Skeleton className="h-5 w-48" />
            <div className="flex items-center gap-2 flex-wrap">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-12" />
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-muted-foreground">
              Showing page {currentPage} of {totalPages} | Total: {totalRecharges} recharges
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="hover:bg-muted/50"
              >
                Previous
              </Button>
              {/* Show page 1 if not in range */}
              {currentPage > 2 && totalPages > 3 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    className={`${
                      currentPage === 1 
                        ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                        : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    1
                  </Button>
                  {currentPage > 3 && <span className="px-2 text-muted-foreground">...</span>}
                </>
              )}
              {/* Show pages around current page */}
              {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return page;
              })
              .filter((page, index, arr) => arr.indexOf(page) === index) // Remove duplicates
              .filter(page => page >= 1 && page <= totalPages) // Ensure valid range
              .map(page => (
                <Button
                  key={page}
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className={`${
                    currentPage === page 
                      ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                      : 'text-foreground hover:bg-muted/50'
                  }`}
                >
                  {page}
                </Button>
              ))}
              {/* Show last page if not in range */}
              {currentPage < totalPages - 2 && totalPages > 3 && (
                <>
                  {currentPage < totalPages - 3 && <span className="px-2 text-muted-foreground">...</span>}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(totalPages)}
                    className={`${
                      currentPage === totalPages 
                        ? 'bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90' 
                        : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {totalPages}
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="hover:bg-muted/50"
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
